import { TFolderEntry } from '@/api/queries/project/useGetProjectFolderEntries';
import useGetProjectFolders from '@/api/queries/project/useGetProjectFolders';
import { findTypeAndIconFromMimeType } from '@/helpers/fileHelper';
import { useUserHasPermission } from '@/hooks/usePermissions';
import usePreferredDateFormat from '@/hooks/usePreferredDateFormat';
import BaseFolderEntry, {
  TProps as TBaseFolderEntryProps,
} from '@payaca/components/folderEntry/FolderEntry';
import { Draggable } from '@payaca/dnd';
import { DateFormats } from '@payaca/helpers/internationalHelper';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { format } from 'date-fns';
import { motion } from 'motion/react';
import { FC } from 'react';

export type TProps = {
  idOverride?: string;
  disabled?: boolean;
  projectId: string;
  folderEntry: TFolderEntry;
  onDelete: () => void;
  onShare: () => void;
  onRename: () => void;
  onUnshare: () => void;
} & Pick<
  TBaseFolderEntryProps,
  | 'onClick'
  | 'hovered'
  | 'selected'
  | 'onSelectChange'
  | 'className'
  | 'checkbox'
>;

export const FOLDER_ENTRY_DRAG_TYPE = 'folder-entry';
const sharedBadge: TBaseFolderEntryProps['badge'] = {
  text: 'Shared',
  colour: 'green',
  size: 'sm',
  variant: 'soft',
};

const unsignedBadge: TBaseFolderEntryProps['badge'] = {
  text: 'Unsigned',
  colour: 'yellow',
  size: 'sm',
  variant: 'soft',
};

const signedBadge: TBaseFolderEntryProps['badge'] = {
  text: 'Signed',
  colour: 'green',
  size: 'sm',
  variant: 'soft',
};

const DraggableFolderEntry: FC<TProps> = (props) => {
  const { idOverride, folderEntry, disabled, ...rest } = props;

  const baseFolderEntryProps = useGetFolderEntryProps(props);

  if (!baseFolderEntryProps) {
    console.error('Unsupported folder entry type:', folderEntry.__typename);
    return null;
  }

  return (
    <Draggable
      id={idOverride || `folder-entry::${folderEntry.id}`}
      data={{ type: FOLDER_ENTRY_DRAG_TYPE, folderEntryId: folderEntry.id }}
      disabled={disabled}
    >
      <BaseFolderEntry
        // @ts-expect-error - so it's expected as="button" but motion.button isn't recognized as such
        as={motion.button}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        {...baseFolderEntryProps}
        {...rest}
      />
    </Draggable>
  );
};

const useGetFolderEntryProps = (args: {
  folderEntry: TFolderEntry;
  projectId: string;
  onDelete: () => void;
  onShare: () => void;
  onRename: () => void;
  onUnshare: () => void;
}): TBaseFolderEntryProps | undefined => {
  const { folderEntry, projectId, onDelete, onShare, onRename, onUnshare } =
    args;

  const preferredDateFormat = usePreferredDateFormat(DateFormats.SHORT);

  const { projectFolders } = useGetProjectFolders(projectId);

  const parentFolder = projectFolders?.find(
    (folder) => folder.id === folderEntry.parentFolderId && !folder.isRoot
  );

  const isWithinSubFolder = !projectFolders?.find(
    (folder) => folder.id === parentFolder?.parentId
  )?.isRoot;

  const userCanUnshare = useUserHasPermission({
    permissions: [DealsPermissions.UNSHARE_DEAL_FILES],
  });

  type ContextMenuItem = NonNullable<
    TBaseFolderEntryProps['contextMenuItems']
  >[number];

  const unshareMenuItem: ContextMenuItem = {
    label: 'Unshare',
    onClick: (e) => {
      e.stopPropagation();
      onUnshare();
    },
  };

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: 'Rename',
      onClick: (e) => {
        e.stopPropagation();
        onRename();
      },
    },
    {
      label: 'Share',
      disabled: !folderEntry.isShareable.result,
      disabledReasons: folderEntry.isShareable.reasons || undefined,
      onClick: (e) => {
        e.stopPropagation();
        onShare();
      },
    },
    ...(userCanUnshare && folderEntry.isSharedWithCustomer
      ? [unshareMenuItem]
      : []),
    {
      label: 'Delete',
      disabled: !folderEntry.isDeletable.result,
      disabledReasons: folderEntry.isDeletable.reasons || undefined,
      onClick: (e) => {
        e.stopPropagation();
        onDelete();
      },
    },
  ];

  const isShared = folderEntry.isSharedWithCustomer;

  switch (folderEntry.__typename) {
    case 'UploadFolderEntry': {
      const [fileType, thumbnailIconName] = findTypeAndIconFromMimeType(
        folderEntry.upload.mimeType
      );

      return {
        fileName: folderEntry.upload.fileName,
        fileType,
        thumbnailIconName,
        formattedDate: format(folderEntry.updatedAt, preferredDateFormat),
        thumbnailUrl: folderEntry.upload.thumbnailUrl || undefined,
        folder: parentFolder?.name,
        subFolder: isWithinSubFolder,
        contextMenuItems,
        badge: isShared ? sharedBadge : undefined,
      };
    }
    case 'UrlFolderEntry':
      return {
        fileName: folderEntry.url.name || folderEntry.name,
        fileType: 'Link',
        thumbnailIconName: 'globe-01.3',
        formattedDate: format(folderEntry.updatedAt, preferredDateFormat),
        folder: parentFolder?.name,
        subFolder: isWithinSubFolder,
        contextMenuItems,
        badge: isShared ? sharedBadge : undefined,
      };
    case 'DocumentFolderEntry':
      return {
        fileName: folderEntry.name,
        fileType: 'Form',
        formattedDate: format(folderEntry.updatedAt, preferredDateFormat),
        folder: parentFolder?.name,
        subFolder: isWithinSubFolder,
        contextMenuItems,
        badge: isShared
          ? sharedBadge
          : folderEntry.document.completedAt
            ? {
                text: 'Completed',
                colour: 'green',
                size: 'sm',
                variant: 'soft',
              }
            : {
                text: 'Draft',
                colour: 'gray',
                size: 'sm',
                variant: 'outline',
              },
      };
    case 'DocumentInstanceFolderEntry': {
      let badge: TBaseFolderEntryProps['badge'] | undefined;

      if (isShared) {
        if (folderEntry.documentInstance.hasSignatories) {
          badge = folderEntry.documentInstance.isSigned
            ? signedBadge
            : unsignedBadge;
        } else {
          badge = sharedBadge;
        }
      }

      return {
        fileName: folderEntry.name,
        fileType: 'Document',
        formattedDate: format(folderEntry.updatedAt, preferredDateFormat),
        folder: parentFolder?.name,
        subFolder: isWithinSubFolder,
        contextMenuItems,
        badge,
      };
    }
  }
};

export default DraggableFolderEntry;
